import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import Button from 'components/mui/Button';
import BlIcon from 'components/icons/BlIcon';
import { useSendQuestionMutation } from 'store/slices/questionsApi';
import { formatErrors } from 'helpers/forms';
import { sleep } from 'helpers/common';

import s from './styled';

const GetHelpDialog = memo(({ open, onClose: onCloseProp }) => {
  const formikRef = useRef();
  const initialValues = useMemo(() => {
    return {
      question: '',
    };
  }, []);

  const validationSchema = Yup.object({
    question: Yup.string()
      .min(2, 'Min. 2 characters')
      .required('This field is required'),
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sendQuestion] = useSendQuestionMutation();

  const onSubmit = useCallback(
    async (values, formikInner) => {
      try {
        const data = await sendQuestion({ text: values.question }).unwrap();
        if (data) {
          setIsSubmitted(true);
        }
      } catch (error) {
        const { fieldErrors, nonFieldErrors } = formatErrors(error);

        [...fieldErrors, ...nonFieldErrors].forEach((e) => {
          formikInner.setFieldError('question', e.message);
        });
      }
    },
    [sendQuestion],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });
  formikRef.current = formik;

  const onClose = useCallback(async () => {
    onCloseProp?.();
    formik.resetForm();
    await sleep(200);
    setIsSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCloseProp]);

  return (
    <s.Dialog fullWidth open={open} onClose={onClose}>
      <FormikProvider value={formik}>
        <Form>
          {/* {render()} */}
          {!isSubmitted && (
            <>
              <s.DialogTitle align="center">
                <s.DialogTitleCaption
                  variant="caption"
                  component="p"
                  collisionFix={Typography}
                >
                  GET IN TOUCH
                </s.DialogTitleCaption>
                How Can We Help?
              </s.DialogTitle>
              <s.DialogContent>
                <s.Typography
                  variant="body1"
                  $colorPath="background.dark.main"
                  $css="max-width: 400px;"
                >
                  Please describe the problem or question so our team can
                  respond properly.
                </s.Typography>
                <Field
                  component={s.TextField}
                  fullWidth
                  multiline
                  rows={8}
                  placeholder="Include any relevant details to help us assist you quickly"
                  name="question"
                />
              </s.DialogContent>
              <s.DialogActions>
                <Button fullWidth type="submit">
                  Send Message
                </Button>
              </s.DialogActions>
            </>
          )}
          {isSubmitted && (
            <>
              <s.DialogTitle align="center">
                <s.IconSuccess>
                  <BlIcon icon="check" fontSize="large" />
                </s.IconSuccess>
                Message Sent!
              </s.DialogTitle>
              <s.DialogContent $paddingBottom>
                <s.Typography
                  variant="body1"
                  $colorPath="background.dark.main"
                  $css="max-width: 370px;"
                >
                  Your message has been sent and you will receive a response via
                  the email associated with your account.
                </s.Typography>
                <s.Typography
                  variant="caption"
                  $marginTop
                  $css="max-width: 370px;"
                >
                  If you don’t hear back within 24 hours, please check your spam
                  or junk folder just in case.
                </s.Typography>
              </s.DialogContent>
            </>
          )}
        </Form>
      </FormikProvider>
    </s.Dialog>
  );
});

GetHelpDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default GetHelpDialog;
